import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const TestimonialVideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const videosPerPage = 12;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [currentPage, searchTerm, selectedCategory]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: videosPerPage,
        status: 'published'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const response = await fetch(`${API_BASE_URL}/api/testimonial-videos?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch testimonial videos');
      }
      
      const data = await response.json();
      setVideos(data.videos || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching testimonial videos:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/testimonial-videos/meta/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchVideos();
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getYouTubeEmbedUrl = (youtubeUrl) => {
    const videoIdMatch = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return youtubeUrl;
  };

  const openYouTubeInNewTab = (youtubeUrl) => {
    window.open(youtubeUrl, '_blank', 'noopener,noreferrer');
  };

  const playVideoInline = (video) => {
    // Create and show video modal/iframe
    const iframe = document.createElement('iframe');
    iframe.width = '560';
    iframe.height = '315';
    iframe.src = getYouTubeEmbedUrl(video.youtubeUrl) + '?autoplay=1';
    iframe.title = video.title;
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75';
    modal.onclick = () => document.body.removeChild(modal);
    
    const modalContent = document.createElement('div');
    modalContent.className = 'relative bg-white rounded-lg overflow-hidden max-w-4xl mx-4';
    modalContent.onclick = (e) => e.stopPropagation();
    
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.className = 'absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center z-10';
    closeButton.onclick = () => document.body.removeChild(modal);
    
    modalContent.appendChild(closeButton);
    modalContent.appendChild(iframe);
    modal.appendChild(modalContent);
    document.body.appendChild(modal);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Testimonial Videos
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Watch real patient stories and experiences from their medical journeys
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Search and Filter Section */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  {/* Search Form */}
                  <form onSubmit={handleSearch} className="flex gap-2 flex-1">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search testimonial videos..."
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                    />
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Search
                    </button>
                  </form>
                </div>

                {/* Category Filter */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleCategoryFilter('')}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedCategory === '' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryFilter(category)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        selectedCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Videos Grid */}
              {loading ? (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(videosPerPage)].map((_, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                      <div className="aspect-video bg-gray-200"></div>
                      <div className="p-6">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 text-lg font-medium">Error: {error}</div>
                  <p className="text-gray-600 mt-2">Please try again later.</p>
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-600 text-lg">No testimonial videos found.</div>
                  <p className="text-gray-500 mt-2">Try adjusting your search criteria.</p>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                    {videos.map((video) => (
                      <div key={video._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                        {/* Video Thumbnail */}
                        <div className="relative aspect-video overflow-hidden group">
                          <img 
                            src={video.thumbnailUrl} 
                            alt={video.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = "https://dummyimage.com/600x400/eeeeee";
                            }}
                          />
                          {/* Play Button Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <button
                              onClick={() => playVideoInline(video)}
                              className="w-16 h-16 bg-[#1976f2] rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                              aria-label="Play video"
                            >
                              <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        <div className="p-6">
                          <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                            <span className="bg-[#1976f2] text-white px-2 py-1 rounded text-xs">
                              {video.category}
                            </span>
                            <span>{formatDate(video.publishedAt || video.createdAt)}</span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800 mb-3 leading-tight hover:text-[#1976f2] transition-colors">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                              {video.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => openYouTubeInNewTab(video.youtubeUrl)}
                              className="text-[#1976f2] hover:text-blue-700 font-medium inline-flex items-center transition-colors"
                            >
                              Read More <span className="ml-1">→</span>
                            </button>
                            {video.duration && (
                              <span className="text-xs text-gray-400">
                                {video.duration}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      
                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => setCurrentPage(index + 1)}
                          className={`px-4 py-2 rounded-lg ${
                            currentPage === index + 1
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
                <h4 className="text-xl font-semibold text-gray-800 mb-6">Categories</h4>
                <div className="space-y-2">
                  <ul className="space-y-2">
                    <li>
                      <button
                        onClick={() => handleCategoryFilter('')}
                        className={`text-gray-600 hover:text-blue-600 transition-colors block py-1 w-full text-left ${
                          selectedCategory === '' ? 'text-blue-600 font-medium' : ''
                        }`}
                      >
                        All Categories
                      </button>
                    </li>
                    {categories.map((category) => (
                      <li key={category}>
                        <button
                          onClick={() => handleCategoryFilter(category)}
                          className={`text-gray-600 hover:text-blue-600 transition-colors block py-1 w-full text-left ${
                            selectedCategory === category ? 'text-blue-600 font-medium' : ''
                          }`}
                        >
                          {category}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingChatButton />
    </div>
  );
};

export default TestimonialVideosPage;
